/** Statistics and colorimetry over area ROIs. */
import { cctFromXy, xyzToUpVp, xyzToXy } from './color';
import { linearAt, type PipelineContext } from './pipeline';
import { containsPoint, polygonArea, roiBounds, rowSpan, type AreaRoi, type PointRoi } from './roi';
import { basicStats, percentileInPlace } from './stats';
import { CLIP_HIGH, type Plane } from './types';

export interface AreaStats {
  n: number;
  mean: number;
  sd: number;
  min: number;
  max: number;
  median: number;
  p05: number;
  p95: number;
  sum: number;
  cv: number;
  u0: number;
  u1: number;
  michelson: number;
  /** Geometric area of the ROI shape, in pixels^2. */
  areaPx: number;
  clipped: number;
  color: AreaColor | null;
}

export interface AreaColor {
  X: number;
  Y: number;
  Z: number;
  x: number;
  y: number;
  up: number;
  vp: number;
  cct: number;
  duv: number;
  cctValid: boolean;
}

export function shapeArea(r: AreaRoi | PointRoi): number {
  switch (r.kind) {
    case 'rect':
      return Math.abs(r.w * r.h);
    case 'ellipse':
      return Math.PI * Math.abs(r.rx * r.ry);
    case 'polygon':
      return polygonArea(r.pts);
    case 'point':
      return r.size * r.size;
  }
}

/**
 * Statistics of the measurement plane inside the ROI, plus mean colorimetry
 * computed from averaged linear XYZ (uncalibrated, relative).
 */
export function areaStats(r: AreaRoi | PointRoi, plane: Plane, clipMask: Uint8Array | null, ctx: PipelineContext | null): AreaStats {
  const W = plane.fullWidth;
  const H = plane.fullHeight;
  // Single pass over the clipped bounding box into a preallocated buffer.
  const b = roiBounds(r);
  const x0 = Math.max(0, Math.floor(b.x));
  const y0 = Math.max(0, Math.floor(b.y));
  const x1 = Math.min(W - 1, Math.ceil(b.x + b.w));
  const y1 = Math.min(H - 1, Math.ceil(b.y + b.h));
  const cap = Math.max(0, x1 - x0 + 1) * Math.max(0, y1 - y0 + 1);
  const buf = new Float64Array(cap);
  let n = 0;
  let clipped = 0;
  let X = 0;
  let Y = 0;
  let Z = 0;
  let nc = 0;
  const rgb = [0, 0, 0];
  const m = ctx?.enc.rgbToXyz;
  const poly = r.kind === 'polygon';
  for (let py = y0; py <= y1; py++) {
    const span = rowSpan(r, py);
    if (!span) continue;
    const ly = py - plane.oy;
    const rowOk = ly >= 0 && ly < plane.height;
    const a = Math.max(x0, span[0]);
    const e = Math.min(x1, span[1]);
    for (let px = a; px <= e; px++) {
      if (poly && !containsPoint(r, px + 0.5, py + 0.5)) continue;
      const i = py * W + px;
      const lx = px - plane.ox;
      buf[n++] = rowOk && lx >= 0 && lx < plane.width ? plane.data[ly * plane.width + lx] : NaN;
      if (clipMask && clipMask[i] & CLIP_HIGH) clipped++;
      if (ctx && m) {
        linearAt(ctx, i, rgb);
        if (Number.isFinite(rgb[0] + rgb[1] + rgb[2])) {
          X += m[0] * rgb[0] + m[1] * rgb[1] + m[2] * rgb[2];
          Y += m[3] * rgb[0] + m[4] * rgb[1] + m[5] * rgb[2];
          Z += m[6] * rgb[0] + m[7] * rgb[1] + m[8] * rgb[2];
          nc++;
        }
      }
    }
  }
  const vals = buf.subarray(0, n);
  const st = basicStats(vals);
  // Compact finite values in place (typed-array filter with a callback is very slow at 20 M values).
  let nf = 0;
  for (let k = 0; k < n; k++) {
    const v = buf[k];
    if (v === v && v !== Infinity && v !== -Infinity) buf[nf++] = v;
  }
  const finite = buf.subarray(0, nf);
  const median = percentileInPlace(finite, 50);
  const p05 = percentileInPlace(finite, 5);
  const p95 = percentileInPlace(finite, 95);
  let color: AreaColor | null = null;
  if (nc > 0) {
    X /= nc;
    Y /= nc;
    Z /= nc;
    const [x, y] = xyzToXy(X, Y, Z);
    const [up, vp] = xyzToUpVp(X, Y, Z);
    const c = cctFromXy(x, y);
    color = { X, Y, Z, x, y, up, vp, cct: c.cct, duv: c.duv, cctValid: c.valid };
  }
  return {
    n: st.n,
    mean: st.mean,
    sd: st.sd,
    min: st.min,
    max: st.max,
    median,
    p05,
    p95,
    sum: st.sum,
    cv: st.sd / st.mean,
    u0: st.min / st.mean,
    u1: st.min / st.max,
    michelson: (st.max - st.min) / (st.max + st.min),
    areaPx: shapeArea(r),
    clipped,
    color,
  };
}
